import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Doctor, Patient } from "@/types";
import { Calendar, Clock, Edit2 } from "lucide-react";
import { formatPatientId } from '@/lib/utils/patientId';
import { safeFormatDateOnly, safeFormatTime } from '@/lib/utils/dateUtils';
import { useState } from 'react';
import { useAuthStore } from '@/lib/auth';
import { usePatientsStore } from '@/lib/stores/patients';
import { useToast } from '@/hooks/use-toast';
import { NameEditDialog } from './NameEditDialog';
import { WhatsAppShareButton } from '@/components/shared/WhatsAppShareButton';

interface PatientPersonalInfoProps {
  patient: Patient;
  doctors: Doctor[];
  handleChangeDoctor: (doctorId: string) => void;
  isDoctor: boolean;
  statusBadgeVariant: string;
}

export function PatientPersonalInfo({ 
  patient, 
  doctors, 
  handleChangeDoctor, 
  isDoctor,
  statusBadgeVariant
}: PatientPersonalInfoProps) {
  const [isNameEditOpen, setIsNameEditOpen] = useState(false);
  const { user } = useAuthStore();
  const { updatePatient } = usePatientsStore();
  const { toast } = useToast();

  // Check if user can edit patient names (admin or staff only)
  const canEditName = user?.role === 'admin' || user?.role === 'staff';

  const handleNameUpdate = async (newName: string, reason: string) => {
    try {
      await updatePatient(patient.id, { name: newName });
      
      // Log the name change for audit purposes
      const auditLog = {
        action: 'patient_name_change',
        patientId: patient.id,
        oldName: patient.name,
        newName: newName,
        changedBy: user?.name || 'Unknown',
        userRole: user?.role || 'unknown',
        reason: reason,
        timestamp: new Date().toISOString(),
        patientIdFormatted: formatPatientId(patient.id)
      };
      
      console.log('Patient name change audit:', auditLog);
      
      // In a real application, you would send this to your audit logging service
      // For now, we'll just log it to console and potentially store in localStorage
      try {
        const existingLogs = JSON.parse(localStorage.getItem('patient_audit_logs') || '[]');
        existingLogs.push(auditLog);
        localStorage.setItem('patient_audit_logs', JSON.stringify(existingLogs.slice(-100))); // Keep last 100 entries
      } catch (storageError) {
        console.warn('Could not save audit log to localStorage:', storageError);
      }
      
      toast({
        title: "Name updated successfully",
        description: `Patient name changed from "${patient.name}" to "${newName}"`
      });
      
      setIsNameEditOpen(false);
    } catch (error) {
      console.error('Error updating patient name:', error);
      toast({
        title: "Error updating name",
        description: "Failed to update patient name. Please try again.",
        variant: "destructive"
      });
    }
  };
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between">
          <CardTitle>Personal Information</CardTitle>
          <Badge variant={statusBadgeVariant as any}>
            {patient.status.charAt(0).toUpperCase() + patient.status.slice(1)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Patient ID</span>
              <span className="bg-hospital-primary/10 px-3 py-1 rounded-full text-hospital-primary font-medium text-sm">
                {formatPatientId(patient.id)}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Name</span>
              <div className="flex items-center gap-2">
                <span>{patient.name}</span>
                {canEditName && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsNameEditOpen(true)}
                    className="h-6 w-6 p-0 hover:bg-gray-100"
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Age</span>
              <span>{patient.age}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Gender</span>
              <span>{patient.gender}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">Contact</span>
              <div className="flex items-center gap-2">
                <span>{patient.contactNumber}</span>
                <WhatsAppShareButton
                  phoneNumber={patient.contactNumber}
                  patientName={patient.name}
                  patientId={patient.id}
                  reason={patient.reason}
                  showLabel={false}
                />
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Registration Date</span>
              <span>{safeFormatDateOnly(patient.registeredAt)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Registration Time</span>
              <span>{safeFormatTime(patient.registeredAt)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Reason for Visit</span>
              <span className="text-right max-w-[200px]">{patient.reason || 'Not specified'}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Doctor</span>
              {!isDoctor ? (
                <Select
                  value={patient.doctorId || ''}
                  onValueChange={handleChangeDoctor}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Assign doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors
                      .map(doctor => (
                        <SelectItem key={doctor.id} value={doctor.id}>
                          {doctor.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <span>
                  {doctors.find(d => d.id === patient.doctorId)?.name || 'Unassigned'}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      
      {/* Name Edit Dialog */}
      <NameEditDialog
        isOpen={isNameEditOpen}
        onClose={() => setIsNameEditOpen(false)}
        currentName={patient.name}
        onUpdate={handleNameUpdate}
      />
    </Card>
  );
}
